import { Box, Flex, Button, Icon } from "orcs-design-system";
import PropTypes from "prop-types";
import { memo } from "react";

import { TagPropType } from "src/custom-prop-types/tag";
import { renderExpandableTagBadges } from "./ExpandableBadgeList.badges";
import { useExpandableBadgeList } from "./useExpandableBadgeList";

const ToggleShowButton = ({ showAll, onClick }) => {
  const icon = showAll ? ["fas", "plus"] : ["fas", "minus"];
  const buttonText = showAll ? "Show all tags" : "Show less tags";
  return (
    <Button
      onClick={onClick}
      ariaLabel={buttonText}
      mt="s"
      small
      variant="ghost"
    >
      <Icon icon={icon} /> {buttonText}
    </Button>
  );
};

ToggleShowButton.propTypes = {
  showAll: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const ExpandableBadgeList = ({
  tags,
  defaultLimit = 8,
  initiallyCollapsed = true,
  onEntityTagClick,
  withAppliedCount,
  small,
  highlighted = false,
  inline = false,
  onClickTagInfo,
  entityType,
  ariaLabel = "Tags",
}) => {
  const list = useExpandableBadgeList({
    tags,
    defaultLimit,
    initiallyCollapsed,
    onEntityTagClick,
    onClickTagInfo,
    entityType,
  });

  if (!tags || !tags.length) {
    return null;
  }

  const badges = renderExpandableTagBadges({
    visibleTags: list.visibleTags,
    onEntityTagClick,
    canNavigateToTag: list.canNavigateToTag,
    onSelect: list.onSelect,
    withAppliedCount,
    small,
    highlighted,
    entityType,
    shouldShowInfo: list.shouldShowInfo,
    onInfo: list.onInfo,
    inline,
  });

  if (inline) {
    return (
      <Box role="list" aria-label={ariaLabel} style={{ display: "contents" }}>
        {badges}
      </Box>
    );
  }

  return (
    <>
      <Flex gap="3px" flexWrap="wrap" role="list" aria-label={ariaLabel}>
        {badges}
      </Flex>
      {list.shouldShowBtn && (
        <ToggleShowButton showAll={list.isCollapsed} onClick={list.onClick} />
      )}
    </>
  );
};

ExpandableBadgeList.propTypes = {
  tags: PropTypes.arrayOf(TagPropType),
  defaultLimit: PropTypes.number,
  initiallyCollapsed: PropTypes.bool,
  onEntityTagClick: PropTypes.func,
  withAppliedCount: PropTypes.bool,
  small: PropTypes.bool,
  inline: PropTypes.bool,
  highlighted: PropTypes.bool,
  onClickTagInfo: PropTypes.func,
  entityType: PropTypes.string,
  ariaLabel: PropTypes.string,
};

export default memo(ExpandableBadgeList);
